import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DashboardService } from '../../services';
import {
  DashboardState,
  DashboardStats,
  DashboardOverviewResponse,
  DashboardAnalyticsResponse,
  DashboardQueryParams,
  AnalysisTrend,
  RiskDistribution,
  TopBrandsCategories,
  SystemMetrics,
  RecentActivity,
} from '../../types/api/dashboard';
import { Analysis } from '../../types/api/analyses';

// Initial state
const initialState: DashboardState = {
  stats: null,
  recentAnalyses: [],
  recentActivities: [],
  trends: null,
  riskDistribution: null,
  topItems: null,
  systemMetrics: null,
  selectedPeriod: 'week',
  loading: false,
  error: null,
};

export const fetchDashboardOverview = createAsyncThunk(
  'dashboard/fetchDashboardOverview',
  async (params: DashboardQueryParams = {}, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getDashboardOverview(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch dashboard overview');
    }
  }
);

export const fetchDashboardStats = createAsyncThunk(
  'dashboard/fetchDashboardStats',
  async (params: DashboardQueryParams = {}, { rejectWithValue }) => {
    try {
      const stats = await DashboardService.getDashboardStats(params);
      return stats;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch dashboard stats');
    }
  }
);

export const fetchRecentAnalyses = createAsyncThunk(
  'dashboard/fetchRecentAnalyses',
  async (limit: number, { rejectWithValue }) => {
    try {
      const analyses = await DashboardService.getRecentAnalyses(limit);
      return analyses;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch recent analyses');
    }
  }
);

export const fetchRecentActivities = createAsyncThunk(
  'dashboard/fetchRecentActivities',
  async (limit: number = 20, { rejectWithValue }) => {
    try {
      const activities = await DashboardService.getRecentActivities(limit);
      return activities;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch recent activities');
    }
  }
);

export const fetchDashboardAnalytics = createAsyncThunk(
  'dashboard/fetchDashboardAnalytics',
  async (params: DashboardQueryParams = {}, { rejectWithValue }) => {
    try {
      const analytics = await DashboardService.getDashboardAnalytics(params);
      return analytics;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch dashboard analytics');
    }
  }
);

export const fetchAnalysisTrends = createAsyncThunk(
  'dashboard/fetchAnalysisTrends',
  async (params: DashboardQueryParams = {}, { rejectWithValue }) => {
    try {
      const trends = await DashboardService.getAnalysisTrends(params);
      return trends;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch analysis trends');
    }
  }
);

export const fetchRiskDistribution = createAsyncThunk(
  'dashboard/fetchRiskDistribution',
  async (params: DashboardQueryParams = {}, { rejectWithValue }) => {
    try {
      const distribution = await DashboardService.getRiskDistribution(params);
      return distribution;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch risk distribution');
    }
  }
);

export const fetchTopBrandsCategories = createAsyncThunk(
  'dashboard/fetchTopBrandsCategories',
  async (params: DashboardQueryParams = {}, { rejectWithValue }) => {
    try {
      const topItems = await DashboardService.getTopBrandsCategories(params);
      return topItems;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch top brands and categories');
    }
  }
);

export const fetchSystemMetrics = createAsyncThunk(
  'dashboard/fetchSystemMetrics',
  async (_, { rejectWithValue }) => {
    try {
      const metrics = await DashboardService.getSystemMetrics();
      return metrics;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch system metrics');
    }
  }
);

export const exportDashboardData = createAsyncThunk(
  'dashboard/exportDashboardData',
  async (
    { format, params }: { format: 'pdf' | 'excel' | 'csv'; params?: DashboardQueryParams },
    { rejectWithValue }
  ) => {
    try {
      const blob = await DashboardService.exportDashboardData(format, params);
      return { blob, format };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to export dashboard data');
    }
  }
);

export const updateDashboardPreferences = createAsyncThunk(
  'dashboard/updateDashboardPreferences',
  async (preferences: Record<string, any>, { rejectWithValue }) => {
    try {
      await DashboardService.updateDashboardPreferences(preferences);
      return preferences;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update preferences');
    }
  }
);

export const fetchDashboardPreferences = createAsyncThunk(
  'dashboard/fetchDashboardPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const preferences = await DashboardService.getDashboardPreferences();
      return preferences;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch preferences');
    }
  }
);

// Dashboard slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedPeriod: (state, action: PayloadAction<string>) => {
      state.selectedPeriod = action.payload;
    },
    resetDashboardState: () => initialState,
    updateRecentAnalyses: (state, action: PayloadAction<Analysis>) => {
      // Add new analysis to the beginning of recent analyses
      state.recentAnalyses.unshift(action.payload);
      // Keep only the latest 10 analyses
      if (state.recentAnalyses.length > 10) {
        state.recentAnalyses = state.recentAnalyses.slice(0, 10);
      }
    },
    addRecentActivity: (state, action: PayloadAction<RecentActivity>) => {
      state.recentActivities.unshift(action.payload);
      // Keep only the latest 20 activities
      if (state.recentActivities.length > 20) {
        state.recentActivities = state.recentActivities.slice(0, 20);
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch dashboard overview
    builder
      .addCase(fetchDashboardOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.stats;
        state.recentAnalyses = action.payload.recent_analyses;
        state.recentActivities = action.payload.recent_activities;
      })
      .addCase(fetchDashboardOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch dashboard stats
    builder
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Fetch recent analyses
    builder
      .addCase(fetchRecentAnalyses.fulfilled, (state, action) => {
        state.recentAnalyses = action.payload;
      })
      .addCase(fetchRecentAnalyses.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Fetch recent activities
    builder
      .addCase(fetchRecentActivities.fulfilled, (state, action) => {
        state.recentActivities = action.payload;
      })
      .addCase(fetchRecentActivities.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Fetch dashboard analytics
    builder
      .addCase(fetchDashboardAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.trends = action.payload.trends;
        state.riskDistribution = action.payload.risk_distribution;
        state.topItems = action.payload.top_items;
        state.systemMetrics = action.payload.system_metrics;
      })
      .addCase(fetchDashboardAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch analysis trends
    builder
      .addCase(fetchAnalysisTrends.fulfilled, (state, action) => {
        state.trends = action.payload;
      })
      .addCase(fetchAnalysisTrends.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Fetch risk distribution
    builder
      .addCase(fetchRiskDistribution.fulfilled, (state, action) => {
        state.riskDistribution = action.payload;
      })
      .addCase(fetchRiskDistribution.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Fetch top brands and categories
    builder
      .addCase(fetchTopBrandsCategories.fulfilled, (state, action) => {
        state.topItems = action.payload;
      })
      .addCase(fetchTopBrandsCategories.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Fetch system metrics
    builder
      .addCase(fetchSystemMetrics.fulfilled, (state, action) => {
        state.systemMetrics = action.payload;
      })
      .addCase(fetchSystemMetrics.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setSelectedPeriod,
  resetDashboardState,
  updateRecentAnalyses,
  addRecentActivity,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;